import { useSiteStore } from '~/stores/site.store';
import { genesysChatIds, genesysQueueName, regions } from 'assets/data/config';
import { useScriptTag } from '@vueuse/core';
import type { GenesysChatMessaging } from '~/interfaces/components/genesys-chat';

export default defineNuxtPlugin({
  dependsOn: ['init-client', 'event-tracking'],
  parallel: true,
  setup() {
    const runtimeConfig = useRuntimeConfig();

    const config = useSiteStore();
    const auth = useAuthStore();
    const locale = useLocaleUtils();

    const { $trackEvent } = useNuxtApp();

    const chatLoaded = ref<boolean>(false);

    const liveChatId = computed(() => genesysChatIds[config.getRegionCode]);
    const liveChatQueue = computed(
      () => genesysQueueName[config.getRegionCode],
    );
    const country = computed(() =>
      regions.find((r) => r.regionCode === config.getRegionCode),
    );

    const liveChatlink = computed(
      () => `/scripts/genesys.js?genesysScriptId=${liveChatId.value}`,
    );

    const hiddenElementIds = ['genesys-thirdparty', 'genesys-messenger'];

    const messaging = computed<GenesysChatMessaging>(() => ({
      customAttributes: {
        Brand: 'Jackpot City',
        Language: locale.getLocale().split('-')[0],
        Country: country.value.name,
        'Business Entity Code': config.getRegionCode,
        IsLoggedIn: `${auth.isLoggedIn}`,
        'Queue Name': liveChatQueue.value,
        ...(auth.isLoggedIn && {
          UserId: auth.currentUser.id,
          'Contact ID': auth.currentUser.id,
        }),
      },
    }));

    const toggleDisplay = (display: 'none' | 'block' = 'none') => {
      hiddenElementIds.forEach((hei) => {
        const el: HTMLElement = document.getElementById(hei);
        if (!!el) el.style.display = display;
      });
    };

    const setGenesysDatabase = () => {
      window.Genesys('command', 'Database.set', {
        messaging: Object.assign({}, messaging.value),
      });
    };

    function applyHooks() {
      window.Genesys('subscribe', 'Messenger.opened', () => {
        toggleDisplay('block');
      });

      window.Genesys('subscribe', 'Messenger.closed', () => {
        $trackEvent('LiveChatEvent', 'LIVE_CHAT_CLOSED');
        toggleDisplay();
      });

      window.Genesys('subscribe', 'Messenger.ready', () => {
        window.messageQueue = Object.assign({}, messaging.value);

        $trackEvent('LiveChatEvent', 'LIVE_CHAT_INIT');
        setGenesysDatabase();
        toggleChat();
      });

      window.Genesys('subscribe', 'Conversations.opened', function () {
        setGenesysDatabase();
      });
    }

    const { load, unload } = useScriptTag(
      liveChatlink.value,
      () => {
        const checkInterval = setInterval(() => {
          const chatEL = document.getElementById('genesys-mxg-frame');
          if (!!chatEL) {
            clearInterval(checkInterval);
            chatLoaded.value = true;
            applyHooks();
            toggleChat();
            config.explicitToggleFeature({
              feature: 'fullscreenLoading',
              toggle: false,
            });
          }
        }, 1000);
      },
      {
        async: true,
        defer: true,
        manual: true,
        attrs: {
          id: 'genesys-jackpotcity',
        },
      },
    );

    const toggleChat = () => {
      try {
        if (chatLoaded.value) {
          window.Genesys('subscribe', 'Conversations.opened', function () {
            setGenesysDatabase();
          });
          setTimeout(() => {
            window.Genesys('command', 'Messenger.open');
          }, 300);
        } else {
          config.explicitToggleFeature({
            feature: 'fullscreenLoading',
            toggle: true,
          });
          load();
        }
      } catch (ex) {
        console.error(ex);
      }
    };

    return {
      provide: {
        toggleChat,
        destroyLiveChat: () => {
          chatLoaded.value = false;
          unload();
        },
      },
    };
  },
});
